import { useState, useEffect, useRef } from "react";
import { useSearchParams, useNavigate, Navigate } from "react-router-dom";
import AfterSalesLayout from "./aftersaleslayout/AfterSalesLayout";
import ApiService from "../../components/Api_services";
import RegisteredReceived from "./registeredreceived/RegisteredReceived";
import {
  Shield,
  ChevronRight,
  ClipboardList,
  HardHat,
  Lock,
  Wrench,
  X,
} from "lucide-react";
import RepairService from "./repairservice/RepairService";
import { useLanguage } from "./LanguageContext";
import {
  buildAfterSalesQuery,
  getAfterSalesApiSerial,
  getAfterSalesSerialType,
} from "./serialTypeUtils";

const AfterSalesService = () => {
  const { trans } = useLanguage();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const sn = atob(searchParams.get("sn") || "");
  const getSerialNo = () => sn;
  const serialNoForApi = getAfterSalesApiSerial(searchParams);
  const serialType = getAfterSalesSerialType(searchParams);

  const [customerData, setCustomerData] = useState("");
  const [isSingle, setIsSingle] = useState("");
  const [registerStatus, setRegisterStatus] = useState("");
  const [registerMessage, setRegisterMessage] = useState("");
  const [normalizedSerialNo, setNormalizedSerialNo] = useState("");
  const [registerHasPinCode, setRegisterHasPinCode] = useState(false);
  const [chosenRole, setChosenRole] = useState("");
  const [loading, setLoading] = useState(true);

  // pin code states
  const inputRefs = useRef([]);
  const [pin, setPin] = useState(["", "", "", "", "", ""]);
  const [pinError, setPinError] = useState(false);
  const [ticketInfo, setTicketInfo] = useState(null);
  const [verifyCode, setVerifyCode] = useState("");
  const hasExistingPinCode =
    registerStatus === 200 &&
    (registerHasPinCode || registerMessage === "Success");

  useEffect(() => {
    const fetchAll = async () => {
      const serialNo = serialNoForApi;
      setLoading(true);

      try {
        const [registerRes, warrantyRes] = await Promise.all([
          ApiService.checkRegisterStatus(serialNo, serialType),
          ApiService.getWarrantyInfo(serialNo, serialType),
        ]);

        if (registerRes) {
          setRegisterStatus(registerRes.status);
          setRegisterMessage(registerRes.message);
          setNormalizedSerialNo(registerRes.serial_no || "");
          setRegisterHasPinCode(
            Boolean(registerRes.has_pin_code ?? registerRes.data?.has_pin_code),
          );
        }

        if (warrantyRes && warrantyRes.status === 200) {
          setCustomerData(warrantyRes.data?.customer_data || null);
          setIsSingle(warrantyRes.data?.is_single ?? null);
        } else if (warrantyRes?.serial_no) {
          setNormalizedSerialNo((prev) => prev || warrantyRes.serial_no);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchAll();
  }, [serialNoForApi, serialType]);

  const handlePinChange = (e, i) => {
    const val = e.target.value;
    if (!val) return;
    const newPin = [...pin];
    newPin[i] = val;
    setPin(newPin);
    setPinError(false);
    if (i < 5) inputRefs.current[i + 1].focus();
    if (i === 5) {
      const entered = [...newPin.slice(0, 5), val].join("");
      checkPin(entered);
    }
  };

  const checkPin = (currentPin) => {
    const entered = Array.isArray(currentPin)
      ? currentPin.join("")
      : String(currentPin);
    if (entered.length < 4) return;

    ApiService.verifyCodeCheck(entered, serialNoForApi, chosenRole, serialType)
      .then((res) => {
        if (res && (res.status === 200 || res.status === "200")) {
          if (chosenRole === "repair") {
            setTicketInfo(res.data);
          } else if (chosenRole === "newinstall") {
            setTicketInfo(1);
          }

          setVerifyCode(entered);
          setPinError(false);
        } else {
          setPinError(true);
        }
      })
      .catch((err) => {
        console.error("verifyCodeCheck error", err);
        setPinError(true);
      });
  };

  const handlePinKeyDown = (e, i) => {
    if (!/[0-9]/.test(e.key) && e.key !== "Backspace") e.preventDefault();
    if (e.key === "Backspace") {
      const newPin = [...pin];
      if (e.target.value) {
        newPin[i] = "";
        setPin(newPin);
      } else if (i > 0) {
        inputRefs.current[i - 1].focus();
      }
    }
    if (e.key === "Enter") {
      const newPin = [...pin];
      newPin[i] = e.target.value || pin[i];
      checkPin(newPin.join(""));
    }
  };

  const handlePinBlur = (i) => {
    if (i === 5) checkPin(pin);
  };

  const resetRepairFlow = () => {
    setChosenRole("");
    setPin(["", "", "", "", "", ""]);
    setPinError(false);
    setTicketInfo(null);
    setVerifyCode("");
  };

  useEffect(() => {
    if (chosenRole === "newinstall" && ticketInfo) {
      navigate(
        `/aftersalesservice/newinstallation${buildAfterSalesQuery(searchParams, { pinCode: verifyCode })}`,
      );
    }
  }, [chosenRole, ticketInfo, navigate, verifyCode]);

  if (loading) {
    return (
      <AfterSalesLayout>
        <div style={{ textAlign: "center", margin: "40px 0" }}>Loading...</div>
      </AfterSalesLayout>
    );
  }

  const isSnNotFound =
    !registerStatus &&
    registerMessage !==
      "Serial Number Registered, But In Verification Process" &&
    registerMessage !==
      "Serial Number Registered (Installation), But In Verification Process" &&
    !customerData;

  const displaySerialNo = normalizedSerialNo || sn || serialNoForApi;
  const snNotFoundSub = (trans.sn_notfound_sub || "").replaceAll(
    "{serial_no}",
    displaySerialNo,
  );

  if (isSnNotFound) {
    return (
      <AfterSalesLayout>
        <div className="as-role-portal-content">
          <div style={{ padding: "0 31.97px" }}>
            <div className="as-role-portal-card-header-container">
              <div className="as-role-portal-card-header">
                {trans.philips_air_cond} {}
              </div>
              <div className="as-role-portal-card-title">
                {trans.serv_portal}
              </div>
              <div className="as-role-portal-card-subtitle">
                {trans.select_option}
              </div>
            </div>
          </div>
        </div>

        <div className="as-sn-not-found-content" id="serial_notfound_modal">
          <div className="as-sn-not-found-container">
            <div style={{ padding: "0 31.97px" }}>
              <div className="as-sn-not-found-card">
                <div className="as-sn-not-found-icon">
                  <X size={36} />
                </div>
                <div className="as-sn-not-found-title">
                  {trans.sn_notfound_title}
                </div>
                <div className="as-sn-not-found-subtitle">{snNotFoundSub}</div>
              </div>
            </div>
          </div>
        </div>
      </AfterSalesLayout>
    );
  }

  return (
    <AfterSalesLayout>
      <div className="as-role-portal-content">
        <div style={{ padding: "0 31.97px" }}>
          <div className="as-role-portal-card-header-container">
            <div className="as-role-portal-card-header">
              {trans.philips_air_cond}
            </div>
            <div className="as-role-portal-card-title">{trans.serv_portal}</div>
            <div className="as-role-portal-card-subtitle">
              {trans.select_option}
            </div>
          </div>

          <div
            className="as-role-portal-card as-blue"
            onClick={() => setChosenRole("customer")}
          >
            <div className="as-role-portal-card-main">
              <div className="as-role-portal-clipboardlist-icon">
                <ClipboardList size={28} />
              </div>
              <div style={{ flex: "1" }}>
                <div style={{ color: "#ffffff" }}>{trans.warr_reg}</div>
                <div style={{ fontSize: "14px" }}>{trans.reg_unit}</div>
              </div>
              <ChevronRight size={20} />
            </div>
          </div>

          <div
            className="as-role-portal-card"
            onClick={() => setChosenRole("newinstall")}
          >
            <div className="as-role-portal-card-main">
              <div className="as-role-portal-hardhat-icon">
                <HardHat size={28} />
              </div>
              <div style={{ flex: "1" }}>
                <div style={{ color: "#0b5ed7" }}>{trans.new_install}</div>
                <div style={{ fontSize: "14px" }}>{trans.scan_serial}</div>
                <div className="as-role-portal-card-pin">
                  <Lock size={12} />
                  {trans.pin_req}
                </div>
              </div>
              <ChevronRight size={20} />
            </div>
          </div>

          <div
            className="as-role-portal-card"
            onClick={() => setChosenRole("repair")}
          >
            <div className="as-role-portal-card-main">
              <div className="as-role-portal-wrench-icon">
                <Wrench size={28} />
              </div>
              <div style={{ flex: "1" }}>
                <div style={{ color: "#e17100" }}>{trans.repair_and_serv}</div>
                <div style={{ fontSize: "14px" }}>{trans.submit_job}</div>
                <div className="as-role-portal-card-pin">
                  <Lock size={12} />
                  {trans.pin_req}
                </div>
              </div>
              <ChevronRight size={20} />
            </div>
          </div>
        </div>
      </div>

      {chosenRole === "customer" && (
        <>
          {registerStatus ? (
            <Navigate
              to={`/aftersalesservice/menucenter${buildAfterSalesQuery(searchParams)}`}
              replace
            />
          ) : registerStatus == false &&
            registerMessage ==
              "Serial Number Registered, But In Verification Process" ? (
            <RegisteredReceived
              serialQuery={buildAfterSalesQuery(searchParams)}
            />
          ) : registerStatus == false &&
            registerMessage ==
              "Serial Number Registered (Installation), But In Verification Process" ? (
            <RegisteredReceived
              isNewInstall={true}
              serialQuery={buildAfterSalesQuery(searchParams)}
            />
          ) : customerData ? (
            customerData.shopee_order_id &&
            customerData.shopee_order_id != "" &&
            isSingle == 0 ? (
              <Navigate
                to={`/aftersalesservice/registerserialno${buildAfterSalesQuery(searchParams)}`}
                replace
              />
            ) : isSingle == 1 ? (
              <Navigate
                to={`/aftersalesservice/registerserialnosingle${buildAfterSalesQuery(searchParams)}`}
                replace
              />
            ) : (
              <Navigate
                to={`/aftersalesservice/registerserialnobrunei${buildAfterSalesQuery(searchParams)}`}
                replace
              />
            )
          ) : null}
        </>
      )}

      {(chosenRole === "repair" || chosenRole === "newinstall") &&
        (chosenRole === "repair" && ticketInfo ? (
          <RepairService
            onClose={resetRepairFlow}
            verifyCode={verifyCode}
            ticketInfo={ticketInfo}
          />
        ) : !ticketInfo ? (
          <div className="as-repair-serv-modal">
            <div className="as-repair-serv-content">
              <div className="as-repair-serv-header">
                <div className="as-repair-serv-icon">
                  <Lock size={32} />
                </div>
                <div className="as-repair-serv-close" onClick={resetRepairFlow}>
                  <X size={20} />
                </div>
              </div>

              <div className="as-repair-serv-main">
                <div className="as-repair-serv-title">
                  {chosenRole === "repair"
                    ? trans.pincode_title
                    : trans.pincode_title_newinstall}
                </div>
                <div className="as-repair-serv-subtitle">
                  {trans.pincode_subtitle}
                </div>

                <div
                  className="as-repair-serv-input"
                  style={{ display: "flex", gap: "8px" }}
                >
                  {[0, 1, 2, 3, 4, 5].map((i) => (
                    <input
                      key={i}
                      type="text"
                      inputMode="numeric"
                      ref={(el) => (inputRefs.current[i] = el)}
                      maxLength={1}
                      value={pin[i]}
                      style={{
                        borderColor: pinError ? "red" : undefined,
                        transition: "border-color 0.2s",
                      }}
                      onKeyDown={(e) => handlePinKeyDown(e, i)}
                      onChange={(e) => handlePinChange(e, i)}
                      onBlur={() => handlePinBlur(i)}
                    />
                  ))}
                </div>

                {pinError && (
                  <div style={{ color: "red", fontSize: "12px" }}>
                    {trans.pincode_error}
                  </div>
                )}

                <div className="as-repair-serv-reminder">
                  {chosenRole === "repair"
                    ? trans.pincode_reminder
                    : trans.pincode_reminder_newinstall}
                </div>
              </div>
            </div>
          </div>
        ) : null)}
    </AfterSalesLayout>
  );
};

export default AfterSalesService;
